import React, { useEffect, useState, useMemo } from "react";
import ReactDOM from "react-dom";
import config from "../config.json";
import useSheetDismiss from "../utils/useSheetDismiss";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { fetchUsers, deleteUser, sendEmail, UpdateUser, getCountryCurrency } from "../service/APIService";
import "../styles/Profile.css";
import "../styles/LoginPage.css";
import appIcon from "../images/pocketappslogo.png";

const Profile = () => {
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [loggedInUserName, setLoggedInUserName] = useState("");
  const [showDeleteSection, setShowDeleteSection] = useState(false);
  const [enteredEmail, setEnteredEmail] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpInput, setOtpInput] = useState("");
  const [serverOtp, setServerOtp] = useState("");
  const [country, setCountry] = useState("");
  const [countries, setCountries] = useState([]);
  const [finalConfirm, setFinalConfirm] = useState(false);
  const [biometricEnabled, setBiometricEnabled] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometryType, setBiometryType] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [statusMessage, setStatusMessage] = useState({ text: "", type: "" });
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const { closing: logoutClosing, requestClose: closeLogoutSheet } = useSheetDismiss(() => setShowLogoutModal(false));
  const [showRateModal, setShowRateModal] = useState(false);
  const { closing: rateClosing, requestClose: closeRateSheet } = useSheetDismiss(() => setShowRateModal(false));
  const [selectedRating, setSelectedRating] = useState(5);

  const navigate = useNavigate();
  const location = useLocation();
  const loggedInUserEmail = localStorage.getItem("loggedInUserEmail");
  const pocketapp_sessionUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("loggedInUser"));
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    const initializeUserSession = async () => {
      if (deleteSuccess) {
        navigate("/");
        return;
      }
      const countryList = getCountryCurrency();
      setCountries(countryList);
      const isBiometricEnabled = localStorage.getItem("biometricEnabled") === "true";
      setBiometricEnabled(isBiometricEnabled);
      const guestUser = JSON.parse(localStorage.getItem("guestUser"));
      const isRealUser = pocketapp_sessionUser && pocketapp_sessionUser.email && pocketapp_sessionUser.email !== "guest" && pocketapp_sessionUser.email.includes("@");

      if (isRealUser) {
        setLoggedInUser(pocketapp_sessionUser.email);
        setLoggedInUserName(pocketapp_sessionUser.name || pocketapp_sessionUser.email.split("@")[0]);
        setCountry((pocketapp_sessionUser.country || "").toLowerCase());
        localStorage.setItem("loggedInUserEmail", pocketapp_sessionUser.email);
        localStorage.removeItem("guestUser");
      } else if (guestUser) {
        setLoggedInUser(guestUser.email || "guest@local");
        setLoggedInUserName(guestUser.name || "Guest User");
        setCountry((guestUser.country || "").toLowerCase());
      } else {
        setLoggedInUser("guest@local");
        setLoggedInUserName("Guest User");
        setCountry("us");
      }
    };
    initializeUserSession();
  }, [deleteSuccess, navigate, loggedInUserEmail, pocketapp_sessionUser]);

  useEffect(() => {
    if (!pocketapp_sessionUser || loggedInUserEmail === "guest") return;
    fetchUsers(pocketapp_sessionUser.email)
      .then((user) => {
        const serverCountry = (user?.country || "").toLowerCase();
        if (!serverCountry) return;
        setCountry(serverCountry);
        if (serverCountry !== (pocketapp_sessionUser.country || "").toLowerCase()) {
          localStorage.setItem("loggedInUser", JSON.stringify({ ...pocketapp_sessionUser, country: serverCountry }));
        }
      })
      .catch(() => {});
  }, [loggedInUserEmail, pocketapp_sessionUser]);

  // Listen to native Biometric (Face ID / Touch ID / Biometrics) messages
  useEffect(() => {
    const handleNativeMessage = (event) => {
      try {
        let data = event.data;
        if (typeof data === "string") {
          data = JSON.parse(data);
        }
        if (!data || !data.type) return;

        if (data.type === "BIOMETRIC_CHECK_RESULT") {
          setBiometricAvailable(!!data.available);
          if (data.biometryType) {
            setBiometryType(data.biometryType);
          }
        } else if (data.type === "BIOMETRIC_VERIFY_RESULT") {
          if (data.success) {
            localStorage.setItem("biometricEnabled", "true");
            setBiometricEnabled(true);
            showToast(
              `${biometryType === "FaceID" ? "Face ID" : biometryType === "TouchID" ? "Touch ID" : "Biometrics"} enabled successfully`,
              "success"
            );
          } else if (data.error && data.error !== "Cancelled.") {
            showToast(data.error, "error");
          }
        }
      } catch {
        // Ignore non-json
      }
    };

    window.addEventListener("message", handleNativeMessage);
    document.addEventListener("message", handleNativeMessage);

    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify({ type: "BIOMETRIC_CHECK" }));
    }

    return () => {
      window.removeEventListener("message", handleNativeMessage);
      document.removeEventListener("message", handleNativeMessage);
    };
  }, [biometryType]);

  // Platform detection for Store Review (iOS App Store vs Android Google Play)
  const isIOSPlatform = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const platformParam = (searchParams.get("platform") || "").toLowerCase();
    if (platformParam === "android") return false;
    if (platformParam === "ios") return true;

    if (typeof window === "undefined" || !navigator) return true;
    const ua = (navigator.userAgent || navigator.vendor || window.opera || "").toLowerCase();

    // Check Android
    if (/android/.test(ua)) return false;

    // Check iOS (iPhone, iPad, iPod, or iPad on iOS 13+ with desktop UA)
    if (/ipad|iphone|ipod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)) {
      return true;
    }

    // Check React Native biometryType signal
    if (biometryType === "TouchID" || biometryType === "FaceID") return true;

    // Desktop Mac vs Windows/Linux
    if (/macintosh|mac os x/.test(ua)) return true;

    return false;
  }, [location.search, biometryType]);

  const storeReviewUrl = useMemo(() => {
    if (isIOSPlatform) {
      const baseIos = config.iosurl || "https://apps.apple.com/us/app/pocket-apps-hub/id6738062260";
      return baseIos.includes("?") ? `${baseIos}&action=write-review` : `${baseIos}?action=write-review`;
    }
    return (
      config.androidurl ||
      "https://play.google.com/store/apps/details?id=com.pocketapps&pcampaignid=web_share"
    );
  }, [isIOSPlatform]);

  // Always an https store link. Custom schemes (itms-apps://, market://) cannot be
  // resolved by a WebView or a desktop browser and crash the in-app WebView; iOS and
  // Android open apps.apple.com / play.google.com in the store app by themselves.
  const storeUrl = useMemo(() => {
    if (isIOSPlatform) {
      return config.iosurl || "https://apps.apple.com/us/app/pocket-apps-hub/id6738062260";
    }
    return config.androidurl || "https://play.google.com/store/apps/details?id=com.pocketapps&pcampaignid=web_share";
  }, [isIOSPlatform]);

  const handleOpenExternalReview = (targetUrl) => {
    const target = targetUrl || storeReviewUrl;
    setShowRateModal(false);

    // Inside the app: ask the native wrapper to open it outside the WebView.
    if (window.ReactNativeWebView) {
      try {
        window.ReactNativeWebView.postMessage(JSON.stringify({ type: "OPEN_EXTERNAL", url: target }));
      } catch (err) {
        console.log("ReactNativeWebView postMessage error:", err);
      }
      // Older app builds ignore that message and nothing happens. If we are still
      // on screen a moment later, load the store page in place instead.
      setTimeout(() => {
        if (document.visibilityState === "visible") {
          window.location.href = target;
        }
      }, 1500);
      return;
    }

    // Plain browser.
    try {
      const win = window.open(target, "_blank", "noopener,noreferrer");
      if (!win) {
        window.location.href = target; // popup blocked
      }
    } catch (e) {
      console.log("Error opening external browser:", e);
      window.location.href = target;
    }
  };

  const handleRateRowClick = (e) => {
    if (e) {
      e.preventDefault();
    }
    setShowRateModal(true);
  };

  const handleToggleBiometric = () => {
    if (biometricEnabled) {
      localStorage.setItem("biometricEnabled", "false");
      setBiometricEnabled(false);
      showToast(
        `${biometryType === "FaceID" ? "Face ID" : biometryType === "TouchID" ? "Touch ID" : "Biometric sign-in"} disabled`,
        "info"
      );
    } else {
      if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({
            type: "BIOMETRIC_VERIFY",
            reason: `Verify your identity to enable ${
              biometryType === "FaceID" ? "Face ID" : biometryType === "TouchID" ? "Touch ID" : "Biometric sign-in"
            }`,
          })
        );
      } else {
        localStorage.setItem("biometricEnabled", "true");
        setBiometricEnabled(true);
        showToast("Biometric sign-in enabled for this device", "success");
      }
    }
  };

  const showToast = (text, type = "info") => {
    setStatusMessage({ text, type });
    setTimeout(() => {
      setStatusMessage({ text: "", type: "" });
    }, 4000);
  };

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  const confirmLogout = () => {
    setShowLogoutModal(false);
    const emailToPreserve = (pocketapp_sessionUser && pocketapp_sessionUser.email !== "guest") ? pocketapp_sessionUser.email : (loggedInUserEmail !== "guest" ? loggedInUserEmail : "");
    if (emailToPreserve) {
      localStorage.setItem("rememberedEmail", emailToPreserve);
      localStorage.setItem("loggedInUserEmail", emailToPreserve);
    } else {
      localStorage.removeItem("loggedInUserEmail");
    }
    localStorage.removeItem("loggedInUser");
    localStorage.removeItem("guestUser");
    localStorage.removeItem("lastVisitedPage");
    localStorage.setItem("isLoggedOut", "true");
    window.location.href = "/";
  };

  const handleCountryChange = async (selectedCountry) => {
    setCountry(selectedCountry);

    if (loggedInUserEmail === "guest") {
      const guestUser = JSON.parse(localStorage.getItem("guestUser")) || {};
      guestUser.country = selectedCountry;
      localStorage.setItem("guestUser", JSON.stringify(guestUser));
      showToast("Country updated successfully for guest.", "success");
    } else {
      try {
        setLoading(true);
        const user = await fetchUsers(loggedInUserEmail);
        const updatedUser = { ...user, country: selectedCountry };
        await UpdateUser(updatedUser);
        const storedUser = JSON.parse(localStorage.getItem("loggedInUser")) || {};
        storedUser.country = selectedCountry;
        localStorage.setItem("loggedInUser", JSON.stringify(storedUser));
        showToast("Country updated successfully.", "success");
      } catch (error) {
        console.error("Failed to update country:", error);
        showToast("Failed to update country. Please try again.", "error");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSendOtp = async () => {
    if (enteredEmail === loggedInUser) {
      const otp = Math.floor(100000 + Math.random() * 900000).toString();
      const requestDateTime = new Date().toLocaleString();
      const htmlContent = `<p>Your OTP code is: <h2>${otp}</h2></p><p>Requested on: ${requestDateTime}</p>`;

      const emailData = {
        sender: process.env.REACT_APP_SENDEREMAIL,
        recipient: loggedInUser,
        subject: config.apptitle + " Profile Deletion OTP",
        cc: loggedInUser,
        body: htmlContent,
      };

      try {
        setLoading(true);
        await sendEmail(emailData);
        showToast("OTP sent to your email address: " + emailData.recipient, "success");
        setServerOtp(otp);
        setOtpSent(true);
      } catch (error) {
        showToast("Failed to send OTP. Please try again.", "error");
      } finally {
        setLoading(false);
      }
    } else {
      showToast("Entered email does not match logged-in email.", "error");
    }
  };

  const handleDeleteProfile = async () => {
    const isConfirmed = window.confirm("Are you sure you want to permanently delete your profile? ❌");
    if (!isConfirmed) return;
    setLoading(true);
    try {
      if (loggedInUser === enteredEmail && otpInput === serverOtp && finalConfirm) {
        const users = await fetchUsers(enteredEmail);
        if (users.email === loggedInUser) {
          await deleteUser(users.id);
          setLoggedInUser("");
          setLoggedInUserName("");
          localStorage.removeItem("loggedInUser");
          localStorage.setItem("isLoggedOut", true);
          alert("Profile deleted successfully.");
          setDeleteSuccess(true);
        } else {
          showToast("Email did not match. Failed to delete profile.", "error");
        }
      } else {
        showToast("Email or OTP did not match. Please verify details.", "error");
      }
    } catch (error) {
      showToast("Failed to delete user profile.", "error");
    } finally {
      setLoading(false);
    }
  };

  const userInitial = loggedInUserName ? loggedInUserName.charAt(0).toUpperCase() : "U";
  const isGuest = loggedInUserEmail === "guest";

  return (
    <div className="profile-container px-3 pt-2">
      {/* Top Header Navigation */}
      <div className="profile-nav-header">
        <button
          type="button"
          className="profile-back-btn"
          onClick={() => {
            if (location.pathname.startsWith("/app1")) {
              navigate("/app1/home");
            } else {
              navigate(-1);
            }
          }}
        >
          <i className="fi fi-rr-arrow-left"></i>
          <span>Back</span>
        </button>

        <span className="fw-bold" style={{ fontSize: "16px", color: "var(--primary-dark)" }}>
          Profile & Account
        </span>

        <div style={{ width: "60px" }}></div>
      </div>

      {/* Status Toast Message */}
      {statusMessage.text && (
        <div
          className={`p-3 mb-3 d-flex align-items-center justify-content-between`}
          style={{
            borderRadius: "var(--radius-sm)",
            backgroundColor: statusMessage.type === "success" ? "#ECFDF5" : "#FEF2F2",
            color: statusMessage.type === "success" ? "#065F46" : "#991B1B",
            border: `1px solid ${statusMessage.type === "success" ? "#A7F3D0" : "#FECACA"}`,
            fontSize: "13px",
            fontWeight: 500,
          }}
        >
          <span>{statusMessage.text}</span>
          <button
            type="button"
            className="btn-close"
            style={{ fontSize: "10px" }}
            onClick={() => setStatusMessage({ text: "", type: "" })}
          />
        </div>
      )}

      {/* User Hero Card */}
      <div className="profile-hero-card">
        <div className="profile-avatar-wrap">
          <span>{userInitial}</span>
        </div>

        <div className="profile-name">{loggedInUserName}</div>
        <div className="profile-email">{loggedInUser}</div>

        <div className="profile-badges-row">
          {isGuest ? (
            <span className="profile-badge profile-badge-guest">
              <i className="fi fi-rr-user"></i> Guest Account
            </span>
          ) : (
            <span className="profile-badge profile-badge-verified">
              <i className="fi fi-rr-check-circle"></i> Verified Account
            </span>
          )}

          {loggedInUser === config.adminemail && (
            <span className="profile-badge profile-badge-admin">
              <i className="fi fi-rr-shield-check"></i> Admin
            </span>
          )}
        </div>

        <button type="button" className="profile-logout-btn" onClick={handleLogoutClick}>
          <i className="fi fi-rr-sign-out-alt"></i>
          <span>Logout</span>
        </button>
      </div>

      {/* Guest Account Sign In Promo */}
      {isGuest && (
        <div className="profile-guest-banner">
          <h5>Unlock Full Sync & Sharing</h5>
          <p>Sign in to sync your expenses seamlessly across all your devices and share with family or partners.</p>
          <button type="button" className="profile-guest-cta border-0" onClick={handleLogoutClick}>
            Sign In
          </button>
        </div>
      )}

      {/* General Settings Group */}
      <div className="profile-group-card">
        <div className="profile-group-title">Preferences & Regional</div>

        {/* Country & Currency Block (Dropdown on separate line) */}
        <div className="profile-item-col">
          <div className="profile-item-left">
            <div className="profile-item-icon-wrap profile-icon-mauve">
              <i className="fi fi-rr-globe"></i>
            </div>
            <div className="profile-item-text">
              <span className="profile-item-title">Country & Currency</span>
              <span className="profile-item-subtitle">
                Formats currency symbols across your records
              </span>
            </div>
          </div>

          <div className="profile-country-select-wrap">
            <select
              value={country}
              onChange={(e) => handleCountryChange(e.target.value)}
              className="profile-country-select-full"
              disabled={loading}
              aria-label="Select Country and Currency"
            >
              <option value="">Select Country & Currency</option>
              {countries.map((item, index) => {
                const countryName = typeof item === "string" ? item : item.country;
                const label = typeof item === "string" 
                  ? item 
                  : `${item.country} (${item.currency} · ${item.code})`;
                return (
                  <option key={index} value={countryName.toLowerCase()}>
                    {label}
                  </option>
                );
              })}
              <option value="other">Other</option>
            </select>
            <i className="fi fi-rr-angle-small-down profile-country-select-chevron"></i>
          </div>
        </div>

        {/* Biometric Face ID / Touch ID Row */}
        {(biometricAvailable || !!window.ReactNativeWebView) && (
          <div className="profile-item-row">
            <div className="profile-item-left">
              <div className="profile-item-icon-wrap profile-icon-mauve">
                <i
                  className={
                    biometryType === "TouchID"
                      ? "fi fi-rr-fingerprint"
                      : "fi fi-rr-face-viewfinder"
                  }
                ></i>
              </div>
              <div className="profile-item-text">
                <span className="profile-item-title">
                  {biometryType === "FaceID"
                    ? "Face ID"
                    : biometryType === "TouchID"
                    ? "Touch ID"
                    : "Face ID / Biometrics"}
                </span>
                <span className="profile-item-subtitle">
                  Quick and secure sign in with your device
                </span>
              </div>
            </div>
            <div className="profile-item-right">
              <label className="profile-switch" aria-label="Toggle Face ID or Biometric authentication">
                <input
                  type="checkbox"
                  checked={biometricEnabled}
                  onChange={handleToggleBiometric}
                />
                <span className="profile-switch-slider"></span>
              </label>
            </div>
          </div>
        )}

        {/* Support & Feedback Row */}
        <Link
          to={location.pathname.startsWith("/app1") ? "/app1/support" : "/manage/contact"}
          className="profile-item-row"
        >
          <div className="profile-item-left">
            <div className="profile-item-icon-wrap profile-icon-coral">
              <i className="fi fi-rr-headset"></i>
            </div>
            <div className="profile-item-text">
              <span className="profile-item-title">Help & Feedback</span>
              <span className="profile-item-subtitle">
                Report an issue or reach out to support
              </span>
            </div>
          </div>
          <div className="profile-item-right">
            <i className="fi fi-rr-angle-small-right text-muted" style={{ fontSize: "16px" }}></i>
          </div>
        </Link>

        {/* Rate and Review Row — hidden until config.showRateApp is true */}
        {config.showRateApp && (
            <div
            role="button"
            tabIndex={0}
            className="profile-item-row"
            onClick={handleRateRowClick}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleRateRowClick(e);
              }
            }}
            title={isIOSPlatform ? "Rate and review on App Store" : "Rate and review on Google Play"}
            style={{ cursor: "pointer" }}
          >
            <div className="profile-item-left">
              <div
                className={`profile-item-icon-wrap ${
                  isIOSPlatform ? "profile-icon-apple" : "profile-icon-googleplay"
                }`}
              >
                {isIOSPlatform ? (
                  <i className="fab fa-apple" style={{ fontSize: "20px" }}></i>
                ) : (
                  <i className="fab fa-google-play" style={{ fontSize: "16px" }}></i>
                )}
              </div>
              <div className="profile-item-text">
                <span className="profile-item-title">Rate and Review</span>
                <span className="profile-item-subtitle">
                  {isIOSPlatform
                    ? "Share your feedback on the App Store"
                    : "Share your feedback on Google Play"}
                </span>
              </div>
            </div>
            <div className="profile-item-right">
              <span className="profile-rating-stars">★★★★★</span>
              <i className="fi fi-rr-angle-small-right text-muted" style={{ fontSize: "16px" }}></i>
            </div>
          </div>
        )}

        {/* Admin Dashboard Row */}
        {loggedInUser === config.adminemail && (
          <a href="/admin/dashboard" className="profile-item-row">
            <div className="profile-item-left">
              <div className="profile-item-icon-wrap profile-icon-blue">
                <i className="fi fi-rr-dashboard"></i>
              </div>
              <div className="profile-item-text">
                <span className="profile-item-title">Admin Dashboard</span>
                <span className="profile-item-subtitle">Manage app metrics & platform stats</span>
              </div>
            </div>
            <div className="profile-item-right">
              <i className="fi fi-rr-angle-small-right text-muted" style={{ fontSize: "16px" }}></i>
            </div>
          </a>
        )}
      </div>

      {/* Danger Zone / Delete Profile (Only for registered users) */}
      {!isGuest && (
        <div className="profile-danger-card">
          <div
            className="profile-danger-header"
            onClick={() => {
              setShowDeleteSection(!showDeleteSection);
              setFinalConfirm(false);
            }}
          >
            <div className="profile-danger-title">
              <i className="fi fi-rr-trash"></i>
              <span>Delete Account & Data</span>
            </div>
            <i
              className={`fi fi-rr-angle-small-${showDeleteSection ? "up" : "down"}`}
              style={{ color: "#DC2626" }}
            ></i>
          </div>

          {showDeleteSection && (
            <div className="profile-danger-content">
              <p className="profile-danger-desc">
                Permanently deletes your user profile and all associated data. This action cannot be undone.
              </p>

              <div className="login-field-group">
                <label className="login-field-label">Confirm your email</label>
                <div className="login-input-wrap">
                  <span className="login-input-icon">
                    <i className="fi fi-rr-envelope"></i>
                  </span>
                  <input
                    type="email"
                    value={enteredEmail}
                    placeholder="Enter your email address"
                    onChange={(e) => setEnteredEmail(e.target.value)}
                    className="login-input-field"
                  />
                </div>
                <button
                  type="button"
                  className="btn-coral-modern w-100 mt-2"
                  style={{ padding: "10px 14px", fontSize: "13px" }}
                  onClick={handleSendOtp}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <i className="fas fa-spinner fa-spin me-2"></i> Sending OTP...
                    </>
                  ) : otpSent ? (
                    "Resend OTP"
                  ) : (
                    "Send Verification OTP"
                  )}
                </button>
              </div>

              {otpSent && (
                <div className="login-field-group mt-3">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <label className="login-field-label mb-0">Verification Code</label>
                    <button
                      type="button"
                      onClick={handleSendOtp}
                      className="login-forgot-link"
                      disabled={loading}
                    >
                      {loading ? "Sending..." : "Resend Code"}
                    </button>
                  </div>
                  <div className="login-input-wrap">
                    <span className="login-input-icon">
                      <i className="fi fi-rr-shield-check"></i>
                    </span>
                    <input
                      type="tel"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      autoComplete="one-time-code"
                      value={otpInput}
                      placeholder="Enter 6-digit OTP"
                      onChange={(e) => {
                        const numericOnly = e.target.value.replace(/\D/g, "").slice(0, 6);
                        setOtpInput(numericOnly);
                      }}
                      onKeyDown={(e) => {
                        if (
                          !/[0-9]/.test(e.key) &&
                          !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key) &&
                          !e.metaKey &&
                          !e.ctrlKey
                        ) {
                          e.preventDefault();
                        }
                      }}
                      maxLength={6}
                      autoFocus
                      className="login-input-field"
                      style={{ letterSpacing: "4px", fontWeight: "600", fontSize: "16px" }}
                    />
                  </div>
                  <small style={{ fontSize: "11.5px", color: "var(--text-muted)", marginTop: "4px", display: "block" }}>
                    A verification code has been sent to <strong>{loggedInUser}</strong>
                  </small>
                  <button
                    type="button"
                    className="btn btn-outline-danger w-100 mt-3"
                    style={{ borderRadius: "var(--radius-sm)", padding: "10px 14px", fontWeight: 600, fontSize: "13px" }}
                    onClick={() => {
                      setFinalConfirm(true);
                      setShowDeleteSection(false);
                    }}
                  >
                    Verify & Proceed
                  </button>
                </div>
              )}
            </div>
          )}

          {finalConfirm && (
            <div className="profile-danger-content">
              <p className="text-danger fw-bold mb-2">
                Are you absolutely sure you want to permanently delete your profile?
              </p>
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-light flex-grow-1"
                  style={{ borderRadius: "var(--radius-sm)", fontSize: "13px", fontWeight: 600 }}
                  onClick={() => setFinalConfirm(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger flex-grow-1"
                  style={{ borderRadius: "var(--radius-sm)", fontSize: "13px", fontWeight: 600 }}
                  onClick={handleDeleteProfile}
                  disabled={loading}
                >
                  {loading ? "Deleting..." : "Yes, Delete Profile"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Themed Logout Confirmation Bottom Sheet - Portal to escape stacking context */}
      {showLogoutModal && ReactDOM.createPortal(
        <div className="bottom-sheet-overlay" onClick={closeLogoutSheet}>
          <div className={`bottom-sheet-content${logoutClosing ? " sheet-closing" : ""}`} onClick={(e) => e.stopPropagation()}>
            <div className="bottom-sheet-handle" onClick={closeLogoutSheet}></div>
            <button
              type="button"
              className="bottom-sheet-close-corner"
              onClick={closeLogoutSheet}
              aria-label="Close"
              title="Close"
            >
              <i className="fi fi-rr-cross"></i>
            </button>

            <div className="text-center py-2">
              <div
                style={{
                  width: "56px",
                  height: "56px",
                  borderRadius: "50%",
                  background: "rgba(239, 135, 103, 0.15)",
                  color: "var(--accent-coral)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "24px",
                  margin: "0 auto 14px auto",
                }}
              >
                <i className="fi fi-rr-sign-out-alt"></i>
              </div>

              <h4 style={{ fontSize: "18px", fontWeight: 700, color: "var(--primary-dark)", marginBottom: "6px" }}>
                Log Out
              </h4>
              <p style={{ fontSize: "13px", color: "var(--text-muted)", maxWidth: "290px", margin: "0 auto 22px auto", lineHeight: "1.4" }}>
                Are you sure you want to log out? You will be redirected to the sign-in screen.
              </p>

              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-light flex-1 py-2"
                  style={{
                    borderRadius: "14px",
                    fontWeight: 600,
                    border: "1px solid var(--border-subtle)",
                    fontSize: "14px",
                  }}
                  onClick={closeLogoutSheet}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-coral-modern flex-1 py-2"
                  style={{
                    borderRadius: "14px",
                    fontSize: "14px",
                    fontWeight: 600,
                  }}
                  onClick={confirmLogout}
                >
                  Log Out
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Rate and Review Bottom Sheet with Prominent Back Button */}
      {config.showRateApp && showRateModal && ReactDOM.createPortal(
        <div className="bottom-sheet-overlay" onClick={closeRateSheet}>
          <div className={`bottom-sheet-content rate-review-sheet${rateClosing ? " sheet-closing" : ""}`} onClick={(e) => e.stopPropagation()}>
            <div className="bottom-sheet-handle" onClick={closeRateSheet}></div>

            {/* Prominent Back Button Header */}
            <div className="rate-sheet-top-header">
              <button
                type="button"
                className="rate-sheet-back-btn"
                onClick={closeRateSheet}
                aria-label="Back to Profile"
              >
                <i className="fi fi-rr-arrow-left"></i>
                <span>Back to Profile</span>
              </button>
              <button
                type="button"
                className="bottom-sheet-close-corner"
                style={{ position: "static" }}
                onClick={closeRateSheet}
                aria-label="Close"
              >
                <i className="fi fi-rr-cross"></i>
              </button>
            </div>

            <div className="text-center py-2">
              <img src={appIcon} alt={config.apptitle} className="rate-sheet-app-icon" />

              <h4 className="rate-sheet-title">Rate {config.apptitle}</h4>
              <p className="rate-sheet-subtitle">
                Your rating helps us keep improving and building new features!
              </p>

              {/* 5-Star Interactive Rating Selector */}
              <div className="rate-stars-row">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    className={`rate-star-button ${star <= selectedRating ? "active" : ""}`}
                    onClick={() => setSelectedRating(star)}
                    aria-label={`Rate ${star} out of 5 stars`}
                  >
                    ★
                  </button>
                ))}
              </div>
              <p className="rate-rating-label">
                {selectedRating === 5 && "★★★★★ Loved it! Excellent experience ❤️"}
                {selectedRating === 4 && "★★★★☆ Great app, very useful 👍"}
                {selectedRating === 3 && "★★★☆☆ It's good, can be improved 🙂"}
                {selectedRating === 2 && "★★☆☆☆ Needs improvements 😐"}
                {selectedRating === 1 && "★☆☆☆☆ Not satisfied 🙁"}
              </p>

              {/* Action Buttons to Open in Store or Browser */}
              <div className="rate-sheet-actions">
                <button
                  type="button"
                  className="rate-btn-store"
                  onClick={() => handleOpenExternalReview(storeUrl)}
                >
                  {isIOSPlatform ? (
                    <>
                      <i className="fab fa-apple" style={{ fontSize: "18px" }}></i>
                      <span>Open in App Store</span>
                    </>
                  ) : (
                    <>
                      <i className="fab fa-google-play" style={{ fontSize: "16px" }}></i>
                      <span>Open in Google Play</span>
                    </>
                  )}
                </button>

                <button
                  type="button"
                  className="rate-btn-browser"
                  onClick={() => handleOpenExternalReview(storeReviewUrl)}
                >
                  <i className="fi fi-rr-globe"></i>
                  <span>Open in Web Browser</span>
                </button>

                <button
                  type="button"
                  className="rate-btn-back-bottom"
                  onClick={closeRateSheet}
                >
                  <i className="fi fi-rr-arrow-left"></i>
                  <span>Back to Profile</span>
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default Profile;
